import {
  INavigatorSupportMessageAttributes,
  INavigatorUserMessageAttributes,
  NavigatorInputType,
  NavigatorMessage,
  NavigatorUserMessageType,
  TripID,
  TwilioConversationMessage,
} from "@cruisen/contract"
import {
  Client as ConversationsClient,
  Conversation,
  JSONObject,
  Message,
  Paginator,
} from "@twilio/conversations"
import { makeAutoObservable, toJS } from "mobx"
import { transformTwilioMessageIntoMessage, TripCollaborator } from "@cruisen/react-shared"
import { NavigatorFile } from "./components/NavigatorInput"
import { _rootStore } from "../../../models/helpers/useStores"
import { Analytics } from "../../../utils/analytics"
import { getTwilioAccessToken } from "./utils/navigatorStates.ts"
import { updateTrip } from "../../../services/firestore/trips.ts"
import Config from "../../../config/index.ts"
import { onTypingEnded } from "./utils/conversationUtils.ts"
import { onTypingStarted } from "./utils/conversationUtils.ts"

export type TypingUser = {
  avatar: string
  firstName: string
  lastName: string
  userId: string
}

const initialMessagesCount = 20

export class TripNavigatorState {
  hasMore: boolean
  loadingMore: boolean
  messages: NavigatorMessage[]
  typingUsersList: TypingUser[]
  messagesPaginator: Paginator<Message> | null
  conversation: Conversation | null
  isLoading: boolean
  navigatorError: boolean
  readMessageIndex?: number | null
  lastVisibleMessage?: NavigatorMessage
  conversationCl: ConversationsClient | null
  scrollButtonVisible: boolean
  scrolledToBottom: boolean
  inputType: "message" | "navigatorAI"
  selectedItem: { isImage: boolean; url: string; name: string } | null
  isPendingLoadMore: boolean
  currentTripId: TripID | null
  activeModal: "filters" | "files" | null
  navigatorMessageTypes: string[]
  private connectionAttempts: number
  private reconnectionTimer: NodeJS.Timeout | null

  constructor() {
    this.messages = []
    this.conversation = null
    this.isLoading = true
    this.typingUsersList = []
    this.messagesPaginator = null
    this.hasMore = true
    this.loadingMore = false
    this.readMessageIndex = null
    this.navigatorError = false
    this.conversationCl = null
    this.scrollButtonVisible = false
    this.scrolledToBottom = false
    this.inputType = "message"
    this.selectedItem = null
    this.isPendingLoadMore = false
    this.currentTripId = null
    this.activeModal = null
    this.navigatorMessageTypes = []
    this.connectionAttempts = 0
    this.reconnectionTimer = null

    window.addEventListener("online", this.handleNetworkStateChange)
    window.addEventListener("offline", this.handleNetworkStateChange)
    document.addEventListener("visibilitychange", this.handleVisibilityChange)

    makeAutoObservable(this)
  }

  clearStore(savedMessages?: NavigatorMessage[], tripId?: TripID) {
    this.messages = savedMessages || []
    this.isLoading = true
    this.typingUsersList = []
    this.messagesPaginator = null
    this.hasMore = true
    this.loadingMore = false
    this.navigatorError = false

    if (this.conversation) this.conversation.removeAllListeners()
    this.conversation = null
    this.readMessageIndex = null
    this.lastVisibleMessage = null

    this.scrollButtonVisible = false
    this.scrolledToBottom = false
    this.inputType = "message"
    this.isPendingLoadMore = false
    this.currentTripId = tripId || null
    this.activeModal = null
    this.connectionAttempts = 0

    if (this.reconnectionTimer) {
      clearTimeout(this.reconnectionTimer)
      this.reconnectionTimer = null
    }

    this.removeEventListeners()
  }

  private removeEventListeners() {
    window.removeEventListener("online", this.handleNetworkStateChange)
    window.removeEventListener("offline", this.handleNetworkStateChange)
    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
  }

  private handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      if (this.navigatorError && this.currentTripId && _rootStore.loggedInUser) {
        this.connectionAttempts = 0
        this.initializeClient(this.conversation?.sid || "", this.currentTripId)
      }
    }
  }

  private handleNetworkStateChange = () => {
    const isOnline = navigator.onLine

    if (isOnline && this.navigatorError && this.currentTripId && _rootStore.loggedInUser) {
      this.connectionAttempts = 0
      this.initializeClient(this.conversation?.sid || "", this.currentTripId)
    }
  }

  setActiveModal(value: "filters" | "files" | null) {
    this.activeModal = value
  }

  toggleActiveModal(value: "filters" | "files" | null) {
    this.activeModal = this.activeModal === value ? null : value
  }

  setNavigatorMessageTypes(value: string[]) {
    this.navigatorMessageTypes = value
  }

  setCurrentTripId(value: TripID) {
    this.currentTripId = value
  }

  setIsPendingLoadMore(value: boolean) {
    this.isPendingLoadMore = value
  }

  setSelectedItem(value: { isImage: boolean; url: string; name: string } | null) {
    this.selectedItem = value
  }

  setInputType(value: "message" | "navigatorAI") {
    this.inputType = value
  }

  setScrollButtonVisible(value: boolean) {
    this.scrollButtonVisible = value
  }

  setScrolledToBottom(value: boolean) {
    this.scrolledToBottom = value
  }

  setNavigatorError(value: boolean) {
    this.navigatorError = value
  }

  setHasMore(value: boolean) {
    this.hasMore = value
  }

  setIsLoading(value: boolean) {
    this.isLoading = value
  }

  setMessagesPaginator(value: Paginator<Message>) {
    this.messagesPaginator = value
  }

  setLoadingMore(value: boolean) {
    this.loadingMore = value
  }

  updateConversation(conv: Conversation) {
    this.conversation = conv
  }

  setMessages(messages: NavigatorMessage[]) {
    this.messages = messages
  }

  addMessage(message: NavigatorMessage) {
    this.messages = [...this.messages, message]
  }

  onChangeText() {
    if (this.conversation)
      this.conversation.typing().catch((error) => console.error("typing error", error))
  }

  addTypingUser(user: TypingUser) {
    if (this.typingUsersList.some((typingUser) => typingUser.userId === user.userId)) {
      return
    }
    this.typingUsersList = [...this.typingUsersList, user]
  }

  removeTypingUser(user: TypingUser) {
    this.typingUsersList = this.typingUsersList.filter(
      (typingUser) => typingUser.userId !== user.userId,
    )
  }

  async sendMessage({
    tripId,
    loggedInUserId,
    firstName,
    lastName,
    avatar,
    message,
    inputType,
    fileData,
    isSuperAdmin,
  }: {
    tripId: TripID
    loggedInUserId: string
    firstName: string
    lastName: string
    avatar: string
    message: string
    inputType: NavigatorInputType
    fileData: NavigatorFile | null
    isSuperAdmin?: boolean
  }) {
    if (message.trim().length === 0) return

    let userMessageType: NavigatorUserMessageType
    switch (inputType) {
      case "navigatorAI":
        userMessageType = "navigatorQuestion"
        break
      case "support":
        userMessageType = "support"
        break
      case "message":
      default:
        userMessageType = "userMessage"
    }

    const attributes: INavigatorUserMessageAttributes | INavigatorSupportMessageAttributes = {
      source: isSuperAdmin ? "support" : "user",
      type: userMessageType,
      userId: loggedInUserId,
      tripId,
      firstName,
      lastName,
      avatar,
    }

    if (fileData) {
      attributes.type = "fileMessage"
      attributes.file = fileData
    }

    if (inputType === "navigatorAI") {
      Analytics.logAskAI()
    }

    return this.conversation!.sendMessage(message, attributes as JSONObject).catch((error) => {
      console.error("message send failure", { error })
    })
  }

  handleReconnection(twilioConversationSid: string, tripId: string, scrollToBottom?: () => void) {
    this.setIsLoading(true)
    const isUserOnline = navigator.onLine
    // There is no reaseon to reconnect if user isn't online
    if (isUserOnline) {
      this.handleConnectionError(twilioConversationSid, tripId, scrollToBottom)
    }
  }

  updateNavigatorStates(conversationClient: ConversationsClient) {
    conversationClient
      .getSubscribedConversations()
      .then((conversations) => {
        const navigatorStates = {}
        for (const item of conversations.items) {
          const lastMessage = item.lastMessage
          navigatorStates[item.uniqueName] = {
            readMessageIndex: item.lastReadMessageIndex ?? 0,
            lastMessageIndex: lastMessage?.index ?? 0,
            lastMessageDate: lastMessage?.dateCreated ? lastMessage.dateCreated.valueOf() : 0,
          }
        }
        _rootStore.setProp("navigatorStates", navigatorStates)
      })
      .catch((error) => {
        console.error("Navigator getSubscribedConversations error", { error })
      })
  }

  initializeConversationListeners(
    conversationClient: ConversationsClient,
    scrollToBottom?: () => void,
  ) {
    const onMessageAdded = (newMessage: Message) => {
      if (newMessage.conversation.sid !== this.conversation?.sid) return
      const scrolledToBottom = this.scrolledToBottom

      const navigatorMessage = transformTwilioMessageIntoMessage(
        newMessage as TwilioConversationMessage,
      )

      const isDuplicate = this.messages.some((msg) => msg.id === newMessage.sid)
      if (isDuplicate) return

      this.trackLastVisibleMessage(navigatorMessage)
      this.addMessage(navigatorMessage)

      const messages = _rootStore.messages.toJSON()
      _rootStore.setProp("messages", {
        ...messages,
        [this.currentTripId]: toJS(this.messages),
      })

      if (Config.environment === "development") {
        updateTrip(this.currentTripId, {
          twilioConversationMessagesCounter: navigatorMessage.index,
          twilioConversationLastMessageDate: navigatorMessage.timestamp,
        })
      }

      const currentCollaborator: TripCollaborator = _rootStore.activeTrip.collaborators.find(
        (collaborator: TripCollaborator) => collaborator.userId === _rootStore.loggedInUser.id,
      )

      if (newMessage.sid === currentCollaborator?.twilioParticipantSid || scrolledToBottom) {
        scrollToBottom()
      }
    }

    conversationClient.on("messageAdded", onMessageAdded)
    conversationClient.on("typingStarted", (participant) => {
      const collaboratorUserData = onTypingStarted(participant, this.conversation?.sid)
      if (collaboratorUserData) {
        this.addTypingUser(collaboratorUserData)
      }
    })
    conversationClient.on("typingEnded", (participant) => {
      const collaboratorUserData = onTypingEnded(participant, this.conversation?.sid)
      if (collaboratorUserData) {
        this.removeTypingUser(collaboratorUserData)
      }
    })
  }

  async removeListeners() {
    if (this.conversationCl) {
      await this.conversationCl.shutdown().catch((error) => {
        console.error("Navigator shutdown error", { error })
      })
      this.conversationCl.removeAllListeners()
    }
    if (this.conversation) {
      this.conversation.removeAllListeners()
    }
  }

  async initializeClient(
    twilioConversationSid: string,
    tripId: string,
    scrollToBottom?: () => void,
  ) {
    this.setIsLoading(true)
    if (!_rootStore.loggedInUser) return

    try {
      const token = await this.getToken()
      if (!token) {
        this.setIsLoading(false)
        return null
      }

      await this.removeListeners()
      const conversationClient = new ConversationsClient(token)

      conversationClient.on("initialized", async () => {
        try {
          if (!this.conversationCl) this.updateNavigatorStates(conversationClient)

          const conv = await this.conversationCl!.getConversationBySid(twilioConversationSid)

          if (!conv) return
          if (tripId !== _rootStore.activeTrip.id) return
          this.updateConversation(conv)
          this.initializeConversationListeners(conversationClient, scrollToBottom)

          const messagesPaginator = await conv.getMessages(initialMessagesCount)

          if (!messagesPaginator) return
          if (tripId !== _rootStore.activeTrip.id) return

          this.setMessagesPaginator(messagesPaginator)
          this.setHasMore(messagesPaginator.hasPrevPage)
          const initialMessages = (messagesPaginator.items as TwilioConversationMessage[]).map(
            transformTwilioMessageIntoMessage,
          )
          this.setCurrentTripId(tripId)
          this.setMessages(initialMessages)
          this.trackLastVisibleMessage(initialMessages[initialMessages.length - 1])

          const messages = _rootStore.messages.toJSON()
          _rootStore.setProp("messages", { ...messages, [tripId]: toJS(initialMessages) })
          this.setNavigatorError(false)
          this.setIsLoading(false)
        } catch (error) {
          console.error("Navigator conversation init failed", { error })
          this.setIsLoading(true)
        }
      })

      conversationClient.on("tokenAboutToExpire", async () => {
        console.warn("Conversation client tokenAboutToExpire")
        const newToken = await this.getToken({ forceRefresh: true })
        if (newToken) {
          await conversationClient.updateToken(newToken)
        }
      })

      conversationClient.on("tokenExpired", async () => {
        console.warn("Conversation client tokenExpired")
        const newToken = await this.getToken({ forceRefresh: true })
        if (newToken) {
          await conversationClient.updateToken(newToken)
        }
      })

      conversationClient.on("initFailed", (data) => {
        console.warn("Conversation client initFailed", data)
        this.handleReconnection(twilioConversationSid, tripId, scrollToBottom)
      })

      conversationClient.on("connectionError", (error) => {
        console.warn("Conversation client connectionError", { error })
        this.handleReconnection(twilioConversationSid, tripId, scrollToBottom)
      })

      this.conversationCl = conversationClient
      this.connectionAttempts = 0
    } catch (error) {
      console.error("Navigator client init failed", { error })
      this.handleReconnection(twilioConversationSid, tripId, scrollToBottom)
      return null
    }
  }

  async getToken({ forceRefresh }: { forceRefresh: boolean } = { forceRefresh: false }) {
    try {
      return await getTwilioAccessToken(forceRefresh)
    } catch (error) {
      console.error("Navigator token get failed", { error })
      this.setNavigatorError(true)
      this.setIsLoading(false)
      return null
    }
  }

  async loadMoreMessages(callback?: () => void) {
    if (this.loadingMore || !this.hasMore || this.messages.length < initialMessagesCount) return
    if (!this.conversation) {
      this.setIsPendingLoadMore(true)
      return
    }

    this.setLoadingMore(true)
    try {
      if (this.messagesPaginator) {
        const nextPage = await this.messagesPaginator.prevPage()
        const newMessages = (nextPage.items as TwilioConversationMessage[]).map(
          transformTwilioMessageIntoMessage,
        )
        this.setMessages([...newMessages, ...this.messages])
        callback?.()
        this.setHasMore(nextPage.hasPrevPage)
        this.setMessagesPaginator(nextPage)
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.setLoadingMore(false)
      this.setIsPendingLoadMore(false)
    }
  }

  trackLastVisibleMessage(message: NavigatorMessage) {
    if (!message) return

    if (!this.lastVisibleMessage || message.index > this.lastVisibleMessage.index) {
      console.log("update last visible message", { ...message })
      this.lastVisibleMessage = message
    }

    this.trackReadMessageIndex(this.lastVisibleMessage?.index)
  }

  trackReadMessageIndex(readMessageIndex: number) {
    if (typeof readMessageIndex !== "number") return

    if (!this.readMessageIndex || readMessageIndex > this.readMessageIndex) {
      this.readMessageIndex = readMessageIndex
      this.advanceLastReadMessageIndex()
    }
  }

  advanceLastReadMessageIndex() {
    const conversation = this.conversation
    const readMessageIndex = this.readMessageIndex
    if (conversation && readMessageIndex !== null) {
      conversation
        .advanceLastReadMessageIndex(readMessageIndex)
        .then(() => {
          const navigatorStates = { ..._rootStore.navigatorStates.toJSON() }

          const lastMessage = conversation.lastMessage
          navigatorStates[_rootStore.activeTrip.id] = {
            readMessageIndex,
            lastMessageIndex: lastMessage?.index ?? 0,
            lastMessageDate: lastMessage?.dateCreated ? lastMessage.dateCreated.valueOf() : 0,
          }

          _rootStore.setProp("navigatorStates", navigatorStates)
        })
        .catch((error) => console.error("could not update last read message index", { error }))
    }
  }

  private handleConnectionError(
    twilioConversationSid: string,
    tripId: string,
    scrollToBottom?: () => void,
  ) {
    if (!_rootStore.loggedInUser) return

    const MAX_RETRIES = 3
    const BASE_DELAY = 1500
    const MAX_DELAY = 15000

    if (this.connectionAttempts >= MAX_RETRIES) {
      console.error("Max retry attempts reached for reconnection", {
        twilioConversationSid,
        tripId,
      })
      this.setNavigatorError(true)
      this.setIsLoading(false)
      return
    }

    this.connectionAttempts++

    if (this.reconnectionTimer) {
      clearTimeout(this.reconnectionTimer)
    }

    const delay = Math.min(BASE_DELAY * Math.pow(2.5, this.connectionAttempts - 1), MAX_DELAY)

    this.reconnectionTimer = setTimeout(async () => {
      console.log(`Attempting to reconnect (attempt ${this.connectionAttempts}/${MAX_RETRIES})`)

      try {
        await this.initializeClient(twilioConversationSid, tripId, scrollToBottom)
      } catch {
        this.handleConnectionError(twilioConversationSid, tripId, scrollToBottom)
      }
    }, delay)
  }
}
